import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import type { ApiSettings } from '../api';
import { clearAdminPassword, holdsClientCredential } from '../api';
import { setLocale, t, useLocale } from '../i18n';
import { NAV_GROUPS, NAV_SHORT_LABEL, SCREENS } from '../navigation';
import { useRuleSetDraft, type RuleSetDraft } from '../hooks/useRuleSetDraft';
import { FeederConfigContext } from '../hooks/useFeederConfig';
import { CustomFieldsScope } from '../hooks/useCustomFields';
import { effectiveFeederConfig } from '../feederFields';
import { canPublish, isViewerOnly, type Session } from '../session';
import { KkpLogo } from './KkpLogo';
import { Icon, navIcon } from './Icons';
// R8 users
import { ADMIN_SCREENS } from '../navigation';
import { canManageUsers } from '../session';
import { ChangePasswordDialog } from './PasswordChange';
import { ConfirmDialog, useTopDialog } from './Dialog';
import { createPortal } from 'react-dom';
// R8b per-rule-set permissions
import { useRuleSets } from '../hooks/useRuleSets';
import { primaryAccess as primaryAccessOf, setAccessIn, type RuleSetAccess } from '../ruleSets';
import { mayOnSet } from '../session';
// Wave 5 rule set workspace
import { PrimarySetContext } from '../hooks/usePrimarySetId';
import { primaryIdIn } from '../ruleSets';

/**
 * The console shell: masthead, sidebar, and the one copy of the rule document every
 * screen works from.
 *
 * The layout is the mockup's, which in turn is bank.kkpfg.com's: a full-width purple
 * header carrying the mark and the identity, and a 218px menu column whose selected item
 * is a solid purple fill rather than an edge stripe. There is no status strip under the
 * header: the draft's clean/dirty state is shown on the Rule Management version row, where
 * the draft is edited.
 *
 * The draft lives HERE rather than inside a screen — switching from Rule Management to Log
 * and back must not silently discard unsaved work.
 */

export interface ShellContext {
  state: RuleSetDraft;
  settings: ApiSettings;
  /** The signed-in identity in cookie mode; `null` in password mode, which has no roles. */
  session: Session | null;
  showToast: (message: string, kind?: 'ok' | 'error') => void;
  // 2c rule sets
  /** The draft of a set other than the primary (`/rule-sets/<set>/…`); absent where a test builds its own context. */
  otherSet?: OtherSetDraft;
  // R8b per-rule-set permissions
  /**
   * The signed-in user's level on the PRIMARY set (`GET /api/rulesets`): `'none'` = hidden from
   * them; undefined = not known (password mode, an older service, not loaded yet) — then the
   * global role decides. Fields and Alerts gate their edits on it (`mayOnSet`).
   */
  primaryAccess?: RuleSetAccess | 'none';
}

// 2c rule sets
/**
 * `state` is always the PRIMARY set — Fields, Alerts, Dashboard and Report work on it. A set
 * opened from Rule Sets gets a draft of its own here, so leaving its page does not lose it
 * either. One such set at a time: opening another while it has changes is refused.
 */
export interface OtherSetDraft {
  /** The set whose draft `state` is; null = none chosen. */
  id: string | null;
  state: RuleSetDraft;
  /** `settings` with `ruleSetId` — every rule call made for this set goes to its routes. */
  settings: ApiSettings;
  choose: (id: string | null) => void;
}

/** Screens read the shared draft through this instead of mounting their own copy. */
export function useShell(): ShellContext {
  return useOutletContext<ShellContext>();
}

export function AppLayout({
  settings,
  session,
  onSignOut,
}: {
  settings: ApiSettings;
  session: Session | null;
  onSignOut: () => void;
}) {
  const [toast, setToast] = useState<{ message: string; kind: 'ok' | 'error' } | null>(null);
  // R8 users: the change-own-password dialog.
  const [changingPassword, setChangingPassword] = useState(false);
  // Re-renders the whole shell — and therefore every screen under <Outlet> — on a switch.
  const locale = useLocale();

  // R8b: the user's level per set comes with the registry listing.
  const { list: registry } = useRuleSets(settings);
  const primaryAccess = primaryAccessOf(registry);

  // Only an account that may change the draft asks the engine about it — a Viewer never does.
  // R8b: …on this set (the check is `edit` on the set: validate).
  const state = useRuleSetDraft(settings, {
    checkRules: primaryAccess !== undefined ? mayOnSet(session, primaryAccess, 'edit') : canPublish(session),
  });

  // 2c rule sets: a second draft, for a set other than the primary.
  const [otherSetId, setOtherSetId] = useState<string | null>(null);
  const otherSettings = useMemo<ApiSettings>(
    () => (otherSetId ? { ...settings, ruleSetId: otherSetId } : settings),
    [settings, otherSetId],
  );
  const otherAccess = setAccessIn(registry, otherSetId);
  const otherState = useRuleSetDraft(otherSettings, {
    checkRules: otherAccess !== undefined ? mayOnSet(session, otherAccess, 'edit') : canPublish(session),
    load: otherSetId !== null,
  });
  const otherSet: OtherSetDraft = { id: otherSetId, state: otherState, settings: otherSettings, choose: setOtherSetId };

  const showToast = useCallback((message: string, kind: 'ok' | 'error' = 'ok') => setToast({ message, kind }), []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Leaving with unsaved rule changes is a real mistake in a control panel.
  // 2c rule sets: …in either draft.
  const anyDirty = state.dirty || otherState.dirty;
  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [anyDirty]);

  // QA-R-02: signing out with an unreleased draft asks first (the draft is only in this browser).
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const signOut = () => {
    setConfirmSignOut(false);
    if (holdsClientCredential()) clearAdminPassword();
    onSignOut();
  };

  const context: ShellContext = { state, settings, session, showToast, otherSet, primaryAccess };
  // Wave 5: the primary's id — the document the shell loads is the primary's.
  const primarySetId = state.loaded?.ruleSetId ?? primaryIdIn(registry) ?? null;

  return (
    <div className="shell">
      <header className="masthead">
        <KkpLogo />
        <span className="masthead-title">{t('shell.brand.name')}</span>
        <span className="spacer" />

        {/*
          EN | ไทย. Two buttons rather than a <select>: the choice is binary and always
          visible, which is what the mockup's header link pair does, and a pressed state
          reads correctly to a screen reader. The Thai autonym is written here and marked
          `lang="th"` rather than stored in the catalogue — `ui.en.json` must stay free of
          Thai characters or `build.mjs` gate 3 fails on the English mockup page.
        */}
        <div className="langswitch" role="group" aria-label={t('locale.switch.label')} data-testid="lang-switch">
          <button type="button" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>
            EN
          </button>
          <button type="button" lang="th" aria-pressed={locale === 'th'} onClick={() => setLocale('th')}>
            ไทย
          </button>
        </div>

        {/*
          Who is signed in, and what that lets them do. The roles are not decoration: they
          are why the Approve button is or is not there, so they stay next to the name — but
          the chip is width-capped and ellipsised, because a long display name plus three
          roles was pushing the bar onto a second row at 1200px. The full text is in `title`.
        */}
        {session && <UserMenu session={session} onChangePassword={() => setChangingPassword(true)} />}

        <button
          type="button"
          className="btn small btn-on-dark"
          data-testid="sign-out"
          // QA-R-02: an unreleased draft lives only in this browser — ask before signing out drops it.
          onClick={() => (anyDirty ? setConfirmSignOut(true) : signOut())}
        >
          <Icon name="logout" />
          {t('btn.signout.label')}
        </button>
      </header>

      <div className="app">
        <aside className="side">
          <nav className="navlist" aria-label={t('shell.nav.aria')}>
            {/* NavLink sets `aria-current` to this value while the route is active and
                omits it otherwise, so `.navbtn[aria-current="true"]` — the mockup's own
                selector for the solid purple item — matches with no extra class. */}
            {/* UX review P2-11: the links under small group headings. A link whose sidebar text is
                shortened ("Transaction" under "Rules") keeps the full screen name as its name. */}
            {NAV_GROUPS.map((group) => {
              const links = group.items.flatMap((id) => {
                const screen = SCREENS.find((s) => s.id === id) ?? ADMIN_SCREENS.find((s) => s.id === id);
                // R8 users: Admin-only entries only on an Admin's menu.
                // Wave 4 API clients: API Keys is Admin-only too.
                if (!screen || ((id === 'users' || id === 'apikeys') && !canManageUsers(session))) return [];
                return [screen];
              });
              if (links.length === 0) return null;
              return (
                <div key={group.id} className="navgroup" role="group" aria-labelledby={`navgroup-${group.id}`}>
                  <div className="navhead" id={`navgroup-${group.id}`}>
                    {t(group.labelKey)}
                  </div>
                  {links.map((screen) => {
                    const short = NAV_SHORT_LABEL[screen.id as keyof typeof NAV_SHORT_LABEL];
                    const icon = navIcon(screen.id);
                    return (
                      <NavLink
                        key={screen.id}
                        to={screen.path}
                        className="navbtn"
                        aria-current="true"
                        aria-label={short ? t(screen.labelKey) : undefined}
                      >
                        {icon && <Icon name={icon} />}
                        {t(short ?? screen.labelKey)}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </aside>

        <div className="main">
          <main className="content">
            {/*
              The ONLY role="alert" in the shell. Screens report their own failures with
              role="status" so a single findByRole('alert') stays unambiguous.
            */}
            {state.error && (
              <div className="banner error" role="alert" style={{ marginBottom: 16 }}>
                {state.error}
              </div>
            )}

            {state.loading && !state.draft && <div className="empty">{t('shell.loading.document')}</div>}

            <FeederConfigContext.Provider value={effectiveFeederConfig(state.draft)}>
              {/* Wave 6: the primary's custom fields; a set's own page scopes its own (`RuleSetRulesRoute`). */}
              <CustomFieldsScope fields={state.draft?.customFields}>
                {/* Wave 5: rule links open `/rule-sets/<primary>/…` when a hit names no set. */}
                <PrimarySetContext.Provider value={primarySetId}>
                  <Outlet context={context} />
                </PrimarySetContext.Provider>
              </CustomFieldsScope>
            </FeederConfigContext.Provider>
          </main>
        </div>
      </div>

      {toast && <Toast message={toast.message} kind={toast.kind} />}

      {confirmSignOut && (
        <ConfirmDialog
          testid="signout-confirm"
          title={t('shell.signout.dirty.title')}
          confirmLabel={t('btn.signout.label')}
          cancelLabel={t('btn.cancel.label')}
          onCancel={() => setConfirmSignOut(false)}
          onConfirm={signOut}
        >
          <p className="small">{t('shell.signout.dirty.body')}</p>
        </ConfirmDialog>
      )}

      {/* R8 users */}
      {changingPassword && session && (
        <ChangePasswordDialog
          settings={settings}
          session={session}
          onClose={() => setChangingPassword(false)}
          onChanged={() => {
            setChangingPassword(false);
            showToast(t('users.password.changed.toast'));
          }}
        />
      )}
    </div>
  );
}

/**
 * The shell's toast. While a modal is open the browser keeps it in the top layer — above any
 * z-index — and the page under it inert, so a toast rendered in the page would sit dimmed
 * behind the scrim. It is rendered INTO the top-most open dialog instead (`useTopDialog`), and
 * moves back to the page when that dialog closes, even when both happen in one update.
 */
function Toast({ message, kind }: { message: string; kind: 'ok' | 'error' }) {
  const top = useTopDialog();
  const el = (
    <div className={`toast ${kind === 'error' ? 'error' : ''}`} role="status" data-testid="toast">
      {message}
    </div>
  );
  return top ? createPortal(el, top) : el;
}

/**
 * Who is signed in: avatar, name and roles, as one button that opens a small menu (the
 * account line and "Change password"). The roles stay visible — they explain why an action
 * is or is not offered — but in the page font, under the name, never as code.
 */
function UserMenu({ session, onChangePassword }: { session: Session; onChangePassword: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  /** The menu's items, in order — what the arrow keys move between. */
  const items = () => [...(pop.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
  // The ARIA menu-button pattern (QA-M-12): opening the menu moves focus to its first item.
  useEffect(() => {
    if (open) items()[0]?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Closing from the keyboard puts focus back where the menu was opened from.
        setOpen(false);
        chip.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const roles = session.roles.join(', ') || t('shell.roles.none');
  return (
    <div className="usermenu" ref={box}>
      <button
        ref={chip}
        type="button"
        className="userchip"
        data-testid="session-user"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${session.user.displayName} · ${session.user.actor} · ${roles}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden="true">
          {session.user.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="userchip-text">
          <span className="userchip-name">{session.user.displayName}</span>
          <span className="userchip-roles">
            <span data-testid="session-roles">{roles}</span>
            {isViewerOnly(session) && ` · ${t('shell.readonly.suffix')}`}
          </span>
        </span>
        <span className="userchip-caret" aria-hidden="true">
          <Icon name="chevron-down" size={14} />
        </span>
      </button>
      {open && (
        <div
          className="usermenu-pop"
          role="menu"
          ref={pop}
          onKeyDown={(e) => {
            // Up/Down move between the items and wrap; Home/End jump; Tab leaves and closes.
            const list = items();
            if (list.length === 0) return;
            const at = list.indexOf(document.activeElement as HTMLElement);
            const go = (i: number) => {
              e.preventDefault();
              list[(i + list.length) % list.length]!.focus();
            };
            if (e.key === 'ArrowDown') go(at + 1);
            else if (e.key === 'ArrowUp') go(at < 0 ? list.length - 1 : at - 1);
            else if (e.key === 'Home') go(0);
            else if (e.key === 'End') go(list.length - 1);
            else if (e.key === 'Tab') setOpen(false);
          }}
        >
          <div className="usermenu-actor">
            <span className="avatar" aria-hidden="true">
              {session.user.displayName.slice(0, 1).toUpperCase()}
            </span>
            <b>{session.user.displayName}</b>
            <span>{session.user.actor}</span>
          </div>
          <button
            type="button"
            role="menuitem"
            data-testid="change-password"
            onClick={() => {
              setOpen(false);
              onChangePassword();
            }}
          >
            <Icon name="key" />
            {t('users.password.menu')}
          </button>
        </div>
      )}
    </div>
  );
}
